import React, { useEffect, useRef } from "react";
import { TriggerNodeIcon } from "../../../icons";
import { NodeSubType } from "../../../models/nodeSubType";
import {
    CustomNodeProps,
    NODE_COLOR,
} from "../../../utilities/flowBuilder.utility";
import { ChildNodes } from "../../flow-builder/customNode";
import { NodeStyle } from "../../flow-builder/style";
import { TriggerNodeStyle } from "../../flow-builder/triggerNode/style";
import {
    Tag,
} from "@bikdotai/bik-component-library";
import SwitchIcon from "../../../icons/switchIcon";
import { getMacroChannelLabel } from "../../../constants/MacroChannels";

/**
 * MacroTriggerNode
 * 
 * A completely separate trigger node for the Macros module.
 * Unlike the original TriggerBlockNode, this component:
 */
const MacroTriggerNode = (props: CustomNodeProps) => {
    const { nodeData, editNode, onHover } = props;
    // Read config directly from React Flow node data (single source of truth)
    const backendConfig = nodeData?.backendConfig;

    const triggerNodeRef = useRef<HTMLDivElement>();

    useEffect(() => {
        triggerNodeRef?.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
        });
    }, []);

    const onClickTrigger = (e: React.MouseEvent) => {
        e.stopPropagation();
    // Use editNode prop from parent MacroCustomNode (no direct setEditNode dependency)
            editNode(
                nodeData?.type,
                props?.id || "N1",
                "macro_starting_point",
                nodeData?.nodeIndex || 0,
                ""
            );
    };

    const channels = backendConfig?.channels ?? nodeData?.channels ?? ["whatsapp"];
    const reviewRequired = backendConfig?.reviewRequired ?? nodeData?.reviewRequired ?? false;

    return (
        <div ref={triggerNodeRef as any}>
            <TriggerNodeStyle
                id="macro-trigger-node"
                onMouseEnter={() => onHover("N1", NodeSubType.MACRO_STARTING_POINT)}
                onMouseLeave={() => onHover(null, undefined)}
                onClick={onClickTrigger}
            >
                <NodeStyle
                    className="customNode flex--column"
                    nodeColor={NODE_COLOR[nodeData?.type] || "#DDCCF0"}
                >
                    {/* Header */}
                    <div className="customNode__header flex justify-content-between align-items-center">
                        <div className="flex">
                            <div className="customNode__header__icon icon-wrapper" style={{ borderRadius: "4px"  }}>
                                <TriggerNodeIcon color={"#731DCF"} />
                            </div>
                            <div style={{ width: 15 }}></div>
                            <span className="customNode__header__title">
                                Starting point
                            </span>
                        </div>
                    </div>

                    {/* Body - Show channel summary */}
                    <div
                        className="flex--column"
                        style={{ marginTop: 16}}
                        onClick={onClickTrigger}
                    >
                        <div>
                            {/* Review status */}
                            {reviewRequired && (
                                <div style={{ marginBottom: 8 }}>
                                    <Tag
                                        tagText="Review before sending"
                                        type="grey"
                                        theme="light"
                                        contentPadding={8}
                                        LeadingIcon={() => <SwitchIcon />}
                                        noBorder={true}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer - Dynamic output handles based on selected channels */}
                    <div className="customNode__footer" onClick={onClickTrigger}>
                        <div className="customNode__child-nodes flex--column">
                            {channels.map((channel: string, idx: number) => (
                                <div className="customNode__childNodes" key={channel}>
                                    <ChildNodes
                                        key={channel}
                                        name={getMacroChannelLabel(channel)}
                                        buttonId={channel}
                                        index={idx}
                                        connectorColor={nodeData?.connectorColor}
                                        nodeType={"Trigger"}
                                        count={channels.length}
                                        isConnectable={true}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </NodeStyle>
            </TriggerNodeStyle>
        </div>
    );
};

export default MacroTriggerNode;
